import StatusTag from "@/components/tags/StatusTag";
import { formatLocaleCurrency, formatLocalePercentageNumber } from "@/utils/formatUtils";
import { isDefined } from "@/utils/utils";
import { Col, Collapse, Row } from "antd";
import classNames from "classnames";
import { t } from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { rowGutter } from "../../../../../common/constants";
import type { UUID } from "../../../../../typings/global";
import { CalculatedCommissionsGroupList } from "../../../components/CalculatedCommissionsGroupList";
import { SplitCalculatedCommission } from "../../../splitcommissions/types";
import { CommissionKind, CommissionsBatchStep } from "../../enums";
import type { CalculatedCommission, Commission } from "../../types";
import { getCalculatedCommissionGroup, getCommissionKindCategory } from "../../utils";
import { CalculatedCommissionsModalForm } from "../forms/CalculatedCommissionsModalForm";

const getLabelForMainCommission = (commission: Commission) => {
  if (commission.splitCommissions.length) {
    const labelType = getCommissionKindCategory(commission.commissionKind ?? CommissionKind.INITIAL);

    return `${t("commissions.batch.enums.commissionKind." + labelType)}: ${t("common.year")} 1 (${formatLocalePercentageNumber(
      100 -
        commission.splitCommissions.reduce((acc, value) => {
          return acc + value.paymentRatio;
        }, 0),
    )}) | ${isDefined(commission.splitPayoutCommissionAmount) ? formatLocaleCurrency(commission.splitPayoutCommissionAmount) : undefined}`;
  }

  return t("commissions.batch.helpers.calculateCommission");
};

interface Props {
  commission: Commission;
  showCalculations: boolean;
  batchId: UUID;
}

export const BatchCommissionExtendedTable = ({ commission, showCalculations, batchId }: Props) => {
  const { t } = useTranslation();

  const [calculationsToUpdate, setCalculationsToUpdate] = useState<{
    calculatedCommissions: CalculatedCommission[] | SplitCalculatedCommission[];
    finalCommissionAmount?: number;
    itemId: UUID;
    type: "splitCalculated" | "calculated";
  }>();

  const mainCalculatedCommissionsGroup = getCalculatedCommissionGroup(commission);

  return (
    <>
      {!!commission.errors.length && (
        <Row
          gutter={rowGutter}
          className={classNames("margin-top-medium", {
            "margin-bottom-medium": !showCalculations || !mainCalculatedCommissionsGroup.length,
          })}
        >
          <Col flex="70px">
            <b>{t("common.errors")}:</b>
          </Col>
          <Col flex="1 1 0">{commission.errors.map((error) => error.description).join(", ")}</Col>
        </Row>
      )}

      {showCalculations && mainCalculatedCommissionsGroup.length ? (
        <Collapse
          defaultActiveKey={[0]}
          items={[
            {
              key: 0,
              label: getLabelForMainCommission(commission),
              children: (
                <CalculatedCommissionsGroupList
                  calculatedCommissionsGroup={mainCalculatedCommissionsGroup}
                  onUpdate={
                    commission.batch.step === CommissionsBatchStep.CALCULATION
                      ? () =>
                          setCalculationsToUpdate({
                            calculatedCommissions: commission.calculatedCommissions,
                            finalCommissionAmount: isDefined(commission.splitPayoutCommissionAmount)
                              ? commission.splitPayoutCommissionAmount
                              : commission.commissionAmount,
                            itemId: commission.id,
                            type: "calculated",
                          })
                      : undefined
                  }
                />
              ),
            },
            ...commission.splitCommissions.map((item, commissionIndex) => {
              const calculatedCommissionsGroup = getCalculatedCommissionGroup(item);
              const errors = [...item.errors, ...item.calculatedCommissions.flatMap((cc) => cc.errors)].map(
                (error) => error.description,
              );

              return {
                key: commissionIndex + 1,
                label: `${t("commissions.splitCommissions.helpers.splitLabel", { year: item.paymentYear })} (${formatLocalePercentageNumber(item.paymentRatio)}) | ${formatLocaleCurrency(item.commissionAmount)}`,
                extra: errors.length ? <StatusTag status="error" tooltip={errors.join(" ")} /> : undefined,
                children: (
                  <CalculatedCommissionsGroupList
                    calculatedCommissionsGroup={calculatedCommissionsGroup}
                    onUpdate={
                      commission.batch.step === CommissionsBatchStep.CALCULATION
                        ? () =>
                            setCalculationsToUpdate({
                              calculatedCommissions: item.calculatedCommissions,
                              finalCommissionAmount: item.commissionAmount,
                              itemId: item.id,
                              type: "splitCalculated",
                            })
                        : undefined
                    }
                  />
                ),
              };
            }),
          ]}
        />
      ) : undefined}

      {calculationsToUpdate ? (
        <CalculatedCommissionsModalForm
          open={!!calculationsToUpdate}
          batchId={batchId}
          itemId={calculationsToUpdate.itemId}
          type={calculationsToUpdate.type}
          calculatedCommissions={calculationsToUpdate.calculatedCommissions}
          finalCommissionAmount={calculationsToUpdate.finalCommissionAmount}
          onFormCancel={() => setCalculationsToUpdate(undefined)}
        />
      ) : undefined}
    </>
  );
};
